import Button from '@pelckmans/elementis/components/button';
import Link from '@pelckmans/elementis/components/link';
import Panel from '@pelckmans/elementis/components/panel';
import Exercise from '@pelckmans/elementis/icons/exercise';
import Pelckmans from '@pelckmans/elementis/icons/pelckmans';
import Smartschool from '@pelckmans/elementis/icons/smartschool';
import User from '@pelckmans/elementis/icons/user';
import Heading from '@pelckmans/elementis/components/heading';
import Paragraph from '@pelckmans/elementis/components/paragraph';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../api';
import useStudioGroup from '../../hooks/useStudioGroup';
import Separator from './components/Separator';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { QR_DEMO_BASE_PATH, useRoutes } from '../../hooks/useRoutes';
import ContentBody from '../../layout/content/ContentBody';
import ContentFooter from '../../layout/content/ContentFooter';
import Footer from '../../layout/footer/Footer';
import CookieAlert from '../../shared-components/CookieAlert';

const oauthUrl = `${API_BASE_URL}/oauth/authorize`;
const BASE = 'PAGES.LOGIN';

const loginProviders = {
  SO: ['pelckmans', 'smartschool'],
  ES: ['pelckmans', 'smartschool'],
  BAO: ['pelckmans', 'bao-student'],
  VO: ['zogezegd-leerkracht', 'zogezegd'],
} as const;

type LoginButtonProps = {
  params: URLSearchParams;
  children: React.ReactNode;
};

const LoginButtons = {
  pelckmans: ({ params, children }: LoginButtonProps) => (
    <Button
      size="lg"
      startIcon={<Pelckmans />}
      as="link"
      prominence="primary"
      href={`${oauthUrl}?provider=pelckmans&${params}`}
      className="option-button"
    >
      {children}
    </Button>
  ),
  smartschool: ({ params, children }: LoginButtonProps) => (
    <Button
      size="lg"
      startIcon={<Smartschool />}
      as="link"
      prominence="neutral"
      href={`${oauthUrl}?provider=smartschool&${params}`}
      className="option-button"
    >
      {children}
    </Button>
  ),
  'bao-student': ({ children }: LoginButtonProps) => (
    <Button
      size="lg"
      startIcon={<Exercise />}
      as="link"
      prominence="secondary"
      href={`${process.env.REACT_APP_ALTERNATE_STUDENT_PLATFORM_URL}`}
      className="option-button"
    >
      {children}
    </Button>
  ),
  'zogezegd-leerkracht': ({ params, children }: LoginButtonProps) => (
    <Button
      size="lg"
      startIcon={<Pelckmans />}
      as="link"
      prominence="primary"
      href={`${oauthUrl}?provider=zogezegd-leerkracht&${params}`}
      className="option-button"
    >
      {children}
    </Button>
  ),
  zogezegd: ({ params, children }: LoginButtonProps) => (
    <Button
      size="lg"
      startIcon={<User />}
      as="link"
      prominence="secondary"
      href={`${oauthUrl}?provider=zogezegd&${params}`}
      className="option-button"
    >
      {children}
    </Button>
  ),
};

const infoLinks: Record<string, string> = {
  ES: 'https://info.pelckmansportail.be/',
  BAO: 'https://www.pelckmans.be/onderwijsniveaus/lager-onderwijs.html',
  VO: 'https://www.pelckmans.be/onderwijsniveaus/volwassenenonderwijs.html',
  SO: 'https://info.pelckmansportaal.be/wat-is-pelckmans-portaal/',
};

export default function Login({ isDemo = false }: { isDemo?: boolean }) {
  const [search] = useSearchParams();
  const returnUrl = search.get('returnUrl');
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const studioGroup = useStudioGroup();
  const { buildAbsolutePath } = useRoutes();
  const location = useLocation();

  const params = useMemo(() => {
    const oauthParams = new URLSearchParams();
    if (returnUrl) {
      oauthParams.set('returnUrl', returnUrl);
    }

    oauthParams.set('language', language);
    oauthParams.set(
      'redirect_uri',
      `${window.location.origin}${buildAbsolutePath(
        ['LOGIN_CALLBACK'],
        undefined,
        isDemo ? QR_DEMO_BASE_PATH : undefined,
      )}`,
    );
    oauthParams.set('register_return_url', window.location.origin);
    oauthParams.set('studioGroup', studioGroup);

    return oauthParams;
  }, [returnUrl, language, buildAbsolutePath, isDemo, studioGroup]);

  useDocumentTitle(t('MODULES.LOGIN.STATE_TITLE'));

  if (studioGroup === 'VO' && location.pathname !== buildAbsolutePath(['LOGIN_VO_TEACHER'])) {
    window.location.replace(oauthUrl + `?${params.toString()}&provider=zogezegd`);
    return null;
  }

  return (
    <div className="page login-page">
      <div className="page-content">
        <div className="main-layout">
          <div className="main-layout-container">
            <div className="content-layout">
              <main>
                <div className="margin-wrapper">
                  <div className="content-header">
                    <div className="max-container">
                      <CookieAlert />
                    </div>
                  </div>
                  <ContentBody>
                    <Panel
                      headerText={t(`${BASE}.HEADER.TEXT`)}
                      headerSubText={t(`${BASE}.HEADER.SUB_TEXT`)}
                      logoProps={{
                        'aria-label': t(`${BASE}.LOGO.A11Y_LABEL`),
                        variant: studioGroup === 'BAO' ? 'logo' : studioGroup,
                        size: studioGroup === 'VO' ? 'sm' : 'md',
                      }}
                      footer={
                        <>
                          <Link to={infoLinks[studioGroup]} target="_blank" rel="noopener noreferrer">
                            {t(`${BASE}.FOOTER.EXPLANATION`)}
                          </Link>
                          <Link
                            to={
                              studioGroup === 'ES'
                                ? 'https://info.pelckmansportail.be/foire-aux-questions/#connecter'
                                : 'https://info.pelckmansportaal.be/aanmelden/'
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t(`${BASE}.FOOTER.HELP`)}
                          </Link>
                        </>
                      }
                    >
                      <div className="login-content">
                        <div className="text">
                          <Heading level={2} removeSpacing="all">
                            {t(`${BASE}.BODY.TITLE`)}
                          </Heading>
                          <Paragraph removeSpacing>{t(`${BASE}.BODY.SUB_TEXT`)}</Paragraph>
                        </div>
                        <div className="actions">
                          <div className="actions-options">
                            {loginProviders[studioGroup as keyof typeof loginProviders].map((provider, index) => {
                              const LoginButton = LoginButtons[provider as keyof typeof LoginButtons];
                              const showSeparator = index === 0 && (studioGroup === 'SO' || studioGroup === 'ES');

                              return (
                                <Fragment key={provider}>
                                  <div className="option">
                                    <LoginButton params={params}>
                                      {t(`${BASE}.ACTIONS.LOGIN_${provider.toUpperCase().replace(/-/g, '_')}.TITLE`)}
                                    </LoginButton>
                                    {(studioGroup === 'VO' || studioGroup === 'BAO') && (
                                      <Paragraph
                                        removeSpacing
                                        className="option-explanation"
                                        data-testid={`${provider}-explanation`}
                                      >
                                        {t(
                                          `${BASE}.ACTIONS.LOGIN_${provider
                                            .toUpperCase()
                                            .replace(/-/g, '_')}.EXPLANATION`,
                                        )}
                                      </Paragraph>
                                    )}
                                  </div>
                                  {showSeparator && <Separator />}
                                </Fragment>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </Panel>
                  </ContentBody>
                  <ContentFooter>
                    <Footer />
                  </ContentFooter>
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
